/**
 * Generic formatters for a full UTC ISO instant (e.g. "2026-07-11T23:59:00.000Z"),
 * rendered in the viewer's local timezone. Date and time parts are formatted
 * separately and joined manually rather than via a single combined
 * Intl.DateTimeFormat call — recent ICU data joins date+time with "at"
 * ("Jul 11, 2026 at 7:59 PM"), which doesn't match this app's copy.
 */

function safeDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * "Jul 11, 7:59 PM" (current year) or "Aug 15 2025, 7:59 PM" (other years).
 * Includes the year only when it differs from the current year, in either
 * direction — same-year dates are unambiguous without it. Pass
 * `showLocalTimeLabel: true` to append " (Your Local Time)" for
 * candidate-facing deadline copy.
 */
export function formatLocalDateTime(iso?: string | null, showLocalTimeLabel = false): string {
  const date = safeDate(iso);
  if (!date) return "—";
  const includeYear = date.getFullYear() !== new Date().getFullYear();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  const datePart = includeYear ? `${month} ${day} ${date.getFullYear()}` : `${month} ${day}`;
  const timePart = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  const suffix = showLocalTimeLabel ? " (Your Local Time)" : "";
  return `${datePart}, ${timePart}${suffix}`;
}

/**
 * Format a UTC ISO instant in a specific IANA timezone, e.g.
 * ``formatInTimezone("2026-07-20T18:29:00.000Z", "Asia/Kolkata")``
 * → ``"Jul 20, 11:59 PM IST"``.
 *
 * Used on admin pages to show each candidate's local deadline.
 */
export function formatInTimezone(utcIso: string | null | undefined, tzName: string, showTzLabel = true): string {
  const date = safeDate(utcIso);
  if (!date) return "—";
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: tzName,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(showTzLabel ? { timeZoneName: "short" as const } : {}),
  };
  try {
    return date.toLocaleString("en-US", opts);
  } catch {
    // Invalid timezone name — fall back to UTC display
    return date.toLocaleString("en-US", { ...opts, timeZone: "UTC" });
  }
}

/** "Jul 11, 2026" — table columns. */
export function formatShortDate(iso?: string | null): string {
  const date = safeDate(iso);
  if (!date) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export { formatRelativeTime } from "../utils/audit";
